// Sorte de feuille « nombres complexes » (lot I5, plugin `complex`) : calculs sur ℂ (formes algébrique,
// trigonométrique, exponentielle, argument) et équations dans ℂ. La vérité vient d'un évaluateur sur ℂ (valeur au
// flottant près) ; les gestes et leurs libellés viennent du pack `analyse.complexes`.

import { isLeaf } from './ast.js';

const C = (re, im = 0) => ({ re, im });
const add = (a, b) => C(a.re + b.re, a.im + b.im);
const mul = (a, b) => C(a.re * b.re - a.im * b.im, a.re * b.im + a.im * b.re);
const div = (a, b) => {
  const d = b.re * b.re + b.im * b.im;
  return d === 0 ? null : C((a.re * b.re + a.im * b.im) / d, (a.im * b.re - a.re * b.im) / d);
};
const cexp = (a) => C(Math.exp(a.re) * Math.cos(a.im), Math.exp(a.re) * Math.sin(a.im));
const clog = (a) => C(Math.log(Math.hypot(a.re, a.im)), Math.atan2(a.im, a.re));
const isReal = (a) => Math.abs(a.im) <= 1e-12 * Math.max(1, Math.abs(a.re));

function cpow(a, b) {
  if (isReal(b) && Number.isInteger(b.re) && Math.abs(b.re) <= 64) {
    let r = C(1),
      k = Math.abs(b.re);
    while (k-- > 0) r = mul(r, a);
    return b.re < 0 ? div(C(1), r) : r;
  }
  if (a.re === 0 && a.im === 0) return C(0);
  if (isReal(a) && a.re > 0 && isReal(b)) return C(a.re ** b.re);
  return cexp(mul(b, clog(a)));
}

const FN = {
  exp: cexp,
  cos: (a) => C(Math.cos(a.re) * Math.cosh(a.im), -Math.sin(a.re) * Math.sinh(a.im)),
  sin: (a) => C(Math.sin(a.re) * Math.cosh(a.im), Math.cos(a.re) * Math.sinh(a.im)),
  ln: clog,
  arg: (a) => (a.re === 0 && a.im === 0 ? null : C(Math.atan2(a.im, a.re))),
};

/** Valeur d'une expression dans ℂ ({ re, im }), ou null. env : nom → { re, im } (i et π sont connus). */
export function cvalue(n, table, env = {}) {
  if (n.t === 'Num') return C(Number(n.p) / Number(n.q));
  if (n.t === 'Sym') {
    if (Object.prototype.hasOwnProperty.call(env, n.name)) return env[n.name];
    if (n.name === 'i') return C(0, 1);
    if (n.name === 'π') return C(Math.PI);
    return null;
  }
  if (isLeaf(n)) return null;
  const ev = table.nodes[n.t]?.eval;
  if (ev === 'call') {
    const f = FN[table.syntax.functions[n.name]?.eval];
    const a = n.k.length === 1 ? cvalue(n.k[0], table, env) : null;
    return f && a ? f(a) : null;
  }
  const ks = n.k.map((c) => cvalue(c, table, env));
  if (ks.some((x) => x === null)) return null;
  if (ev === 'add') return ks.reduce(add, C(0));
  if (ev === 'neg') return C(-ks[0].re, -ks[0].im);
  if (ev === 'mul') return ks.reduce(mul, C(1));
  if (ev === 'div') return div(ks[0], ks[1]);
  if (ev === 'pow') return cpow(ks[0], ks[1]);
  if (ev === 'sqrt') return isReal(ks[0]) && ks[0].re >= 0 ? C(Math.sqrt(ks[0].re)) : null;
  if (ev === 'deg') return C((ks[0].re * Math.PI) / 180);
  return null;
}

export const ceq = (a, b) =>
  !!a &&
  !!b &&
  Math.hypot(a.re - b.re, a.im - b.im) <=
    1e-9 * Math.max(1, Math.hypot(a.re, a.im), Math.hypot(b.re, b.im));

/** « A ou B ou C » (hors parenthèses) → ['A', 'B', 'C'] */
export function splitOr(text) {
  const out = [];
  let depth = 0,
    start = 0;
  const s = String(text);
  for (let i = 0; i < s.length; i++) {
    const c = s[i];
    if (c === '(' || c === '[') depth++;
    else if (c === ')' || c === ']') depth--;
    else if (depth === 0 && s.startsWith(' ou ', i)) {
      out.push(s.slice(start, i).trim());
      start = i + 4;
      i += 3;
    }
  }
  out.push(s.slice(start).trim());
  return out;
}

// points d'essai pour comparer deux équations en z (en plus des solutions)
export const CX_PROBES = [
  C(0.37, 1.21),
  C(-1.43, 0.58),
  C(2.11, -0.73),
  C(-0.29, -1.87),
  C(0.83, 0.17),
];

// ── Classement d'un maillon « a = b » (calcul sur ℂ) ───────────────────────
const has = (n, test) => {
  if (test(n)) return true;
  return !isLeaf(n) && n.k.some((c) => has(c, test));
};
const count = (n, test) =>
  (test(n) ? 1 : 0) + (isLeaf(n) ? 0 : n.k.reduce((s, c) => s + count(c, test), 0));
export const opsOf = (n) => (isLeaf(n) ? 0 : 1 + n.k.reduce((s, c) => s + opsOf(c), 0));

/**
 * Nom du builtin qui justifie le maillon a → b (déjà vérifié égal), ou null (calcul ordinaire).
 * Les rôles viennent de la table (eval), jamais de noms de nœuds en dur.
 */
export function complexGesture(a, b, table) {
  const ev = (n) => table.nodes[n.t]?.eval;
  const fn = (n) => (ev(n) === 'call' ? table.syntax.functions[n.name]?.eval : null);
  const isI = (n) => n.t === 'Sym' && n.name === 'i';
  const iSquare = (n) =>
    (ev(n) === 'pow' && isI(n.k[0]) && n.k[1].t === 'Num' && n.k[1].p === 2) ||
    (ev(n) === 'mul' && n.k.filter(isI).length >= 2);
  const sumIn = (n) => !isLeaf(n) && n.k.some((c) => ev(c) === 'add');
  const expandable = (n) =>
    (ev(n) === 'mul' && n.k.filter((c) => ev(c) === 'add').length >= 1 && n.k.length >= 2) ||
    (ev(n) === 'pow' && ev(n.k[0]) === 'add');
  const exps = (n) => count(n, (x) => fn(x) === 'exp');
  const trig = (n) => count(n, (x) => fn(x) === 'cos' || fn(x) === 'sin');
  const conjDen = (n) =>
    ev(n) === 'div' && has(n.k[1], isI) && !has(b, (x) => ev(x) === 'div' && has(x.k[1], isI));
  if (exps(a) > 0 && trig(b) > trig(a) && exps(b) < exps(a)) return 'cx_euler';
  if (trig(a) > 0 && exps(b) > exps(a) && trig(b) < trig(a)) return 'cx_euler';
  if (trig(b) > 0 && trig(a) === 0 && exps(a) === 0) return 'cx_trig_form';
  if (exps(b) > 0 && exps(a) === 0 && trig(a) === 0) return 'cx_exp_form';
  if (trig(a) > trig(b) && trig(b) === 0) return 'cx_trig_value';
  if (exps(a) > exps(b) && exps(b) >= 1) return 'cx_exp_rules';
  if (exps(a) === 1 && exps(b) === 1 && has(a, (x) => ev(x) === 'pow' && fn(x.k[0]) === 'exp'))
    return 'cx_exp_rules';
  if (trig(a) > 0 && trig(b) > 0 && has(a, (x) => ev(x) === 'pow' && sumIn(x))) return 'cx_moivre';
  if (has(a, conjDen)) return 'cx_conjugate';
  if (has(a, iSquare) && !has(b, iSquare)) return 'cx_i2';
  if (has(a, expandable) && opsOf(b) <= opsOf(a) + 4 && !has(b, expandable)) return 'cx_develop';
  return null;
}
